import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface LeadData {
  name: string;
  email: string;
  phone?: string;
  message?: string;
  property_id?: string;
  source?: string;
}

interface SubmitLeadResult {
  success: boolean;
  message: string;
  isRateLimited?: boolean;
}

export function useSubmitLead() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitLead = async (data: LeadData): Promise<SubmitLeadResult> => {
    setIsSubmitting(true);

    try {
      const response = await supabase.functions.invoke('submit-lead', {
        body: {
          name: data.name.trim(),
          email: data.email.trim().toLowerCase(),
          phone: data.phone?.trim() || undefined,
          message: data.message?.trim() || undefined,
          property_id: data.property_id || undefined,
          source: data.source || 'website',
        },
      });

      // Handle edge function response
      if (response.error) {
        // Check if it's a rate limit error (429)
        if (response.error.message?.includes('429') || 
            response.error.context?.status === 429) {
          return {
            success: false,
            message: 'Muitas tentativas. Tente novamente em alguns minutos.',
            isRateLimited: true,
          };
        }
        
        return {
          success: false,
          message: 'Erro ao enviar mensagem. Tente novamente.',
        };
      }

      // Parse the response data
      const result = response.data as { success?: boolean; message?: string; error?: string };

      if (result.error) {
        // Check for rate limit in response body
        if (result.error.includes('Muitas tentativas')) {
          return {
            success: false,
            message: result.error,
            isRateLimited: true,
          };
        }
        
        return {
          success: false,
          message: result.error,
        };
      }

      return {
        success: true,
        message: result.message || 'Mensagem enviada com sucesso!',
      };

    } catch (error) {
      console.error('Error submitting lead:', error);
      return {
        success: false,
        message: 'Erro ao enviar mensagem. Tente novamente.',
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitLead, isSubmitting };
}